// Copyright © 2019-2026 Focal Systems. All rights reserved.

// Lexical + grammar validator for boolean rule expressions, in two dialects:
// - "python": the schedule rule expressions evaluated with Python's eval
//   (e.g. `month == 'December' and day_of_month == 25`,
//          `any([x.startswith('BK') for x in tag_names])`).
// - "cel": Common Expression Language (`size(x) > 0 && 'BK' in tags`).
//
// It tokenizes the input, then parses it with a precedence-climbing recursive descent
// parser. It validates grammar (operators, calls, member access, collections,
// comprehensions/macros, balanced brackets) — not types or whether identifiers exist.

export type ExpressionDialect = "python" | "cel";

/**
 * What a `warning` is about, so a UI can label it without inferring the category from the payload:
 * - `"property"` — an unrecognised property of a known variable;
 * - `"value"` — a value outside an enum leaf's accepted set (carries `warningOptions`);
 * - `"decimal"` — a decimal-typed variable compared against a whole-number literal;
 * - `"arithmetic"` — arithmetic that CEL rejects at *evaluation* time (see {@link validateExpression});
 * - `"object"` — an object used where a value belongs (`inventory > 1`); `warningOptions` carries its
 *   field names.
 */
export type WarningKind = "property" | "value" | "decimal" | "arithmetic" | "object";

export interface ExpressionValidation {
  valid: boolean;
  error?: string;
  position?: number;
  /** Non-fatal note (e.g. an unrecognised property of a known variable) — does not make it invalid. */
  warning?: string;
  /** The category of `warning`, set whenever `warning` is. */
  warningKind?: WarningKind;
  /**
   * For an enum-mismatch warning, the set of accepted values (kept separate from `warning` so the UI
   * can render them compactly rather than inlining a long list into the message).
   */
  warningOptions?: readonly string[];
}

/**
 * A numeric scalar leaf's value type — `"float"` for a decimal (CEL `double`), `"int"` for a whole
 * number. Knowing both lets arithmetic be checked for mixed operands (see {@link VariableSchema});
 * an untyped (`true`) leaf is simply never involved in a type check.
 */
export type ScalarType = "float" | "int";

/**
 * A nested map of allowed variable properties for member-access checking. A leaf is either:
 * - `true` — a scalar of any value (no further properties allowed);
 * - `"float"` / `"int"` — a numeric scalar of that type. Two checks use it: comparing a `"float"`
 *   against a whole-number literal (`inventory.soh == 3` → suggests `3.0`), which CEL handles
 *   correctly and is only flagged as an intent hint; and mixing the two types in arithmetic, which
 *   CEL rejects at evaluation time;
 * - a `readonly string[]` — an *enum* scalar constrained to those values: comparing it against a
 *   string literal outside the set is flagged as a warning (see {@link validateExpression});
 * a nested object describes an object's properties.
 * e.g. `{ task: { type: true }, date: { day_of_week: ["Monday", …] }, store: { open_time: { hour: true } } }`.
 */
export type VariableSchema = { readonly [key: string]: VariableSchema | true | ScalarType | readonly string[] };

type SchemaNode = VariableSchema | true | ScalarType | readonly string[];

// `true` (untyped scalar), a ScalarType and a string[] (enum) are all leaves — no further properties.
function isLeaf(node: SchemaNode): node is true | ScalarType | readonly string[] {
  return node === true || typeof node === "string" || Array.isArray(node);
}

function isEnumLeaf(node: SchemaNode | null): node is readonly string[] {
  return Array.isArray(node);
}

class ExprError extends Error {
  constructor(
    message: string,
    readonly position: number,
  ) {
    super(message);
  }
}

interface Token {
  kind: "num" | "str" | "name" | "op";
  value: string;
  pos: number;
}

// Whitespace | string | number | identifier | operator/punctuation (multi-char first).
const TOKEN_RE =
  /\s+|('(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*")|(\d+(?:\.\d+)?)|([A-Za-z_][A-Za-z0-9_]*)|(==|!=|<=|>=|&&|\|\||[-+*/%<>!().,[\]{}:?])/y;

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let pos = 0;
  while (pos < input.length) {
    TOKEN_RE.lastIndex = pos;
    const m = TOKEN_RE.exec(input);
    if (!m || m.index !== pos) throw new ExprError("Unexpected character", pos);
    const [full, str, num, name, op] = m;
    if (str !== undefined) tokens.push({ kind: "str", value: str, pos });
    else if (num !== undefined) tokens.push({ kind: "num", value: num, pos });
    else if (name !== undefined) tokens.push({ kind: "name", value: name, pos });
    else if (op !== undefined) tokens.push({ kind: "op", value: op, pos });
    // else: whitespace — skip
    pos += full.length;
  }
  return tokens;
}

interface DialectConfig {
  or: string;
  and: string;
  not: string;
  notLowPrec: boolean; // python: `not` is low precedence; cel: `!` is unary/high
  booleans: Set<string>;
  keywords: Set<string>; // reserved names that cannot be identifiers
  comprehension: boolean; // python list comprehensions + set literals
  ternary: boolean; // cel `a ? b : c`
  keywordComparisons: boolean; // python `not in`, `is`, `is not`
}

const CONFIGS: Record<ExpressionDialect, DialectConfig> = {
  python: {
    or: "or",
    and: "and",
    not: "not",
    notLowPrec: true,
    booleans: new Set(["True", "False", "None"]),
    keywords: new Set(["and", "or", "not", "in", "is", "for", "if", "else", "lambda", "True", "False", "None"]),
    comprehension: true,
    ternary: false,
    keywordComparisons: true,
  },
  cel: {
    or: "||",
    and: "&&",
    not: "!",
    notLowPrec: false,
    booleans: new Set(["true", "false", "null"]),
    keywords: new Set(["in", "true", "false", "null"]),
    comprehension: false,
    ternary: true,
    keywordComparisons: false,
  },
};

const COMPARE_OPS = new Set(["==", "!=", "<", "<=", ">", ">="]);

// Severity order for picking which warning to surface (lower = more severe).
const WARNING_RANK: Record<WarningKind, number> = { arithmetic: 0, object: 0, property: 1, value: 1, decimal: 2 };

interface PathSegment {
  value: string;
  pos: number;
}

// An atom parsed as a comparison operand: a variable member-access path, a string literal, a
// numeric literal, a list of string literals (`['a', 'b']`), the result of an arithmetic operation,
// or anything else (`none`). Used to validate typed variables against the literals they're compared
// with, and to type arithmetic operands. `raw` keeps a numeric literal's source text (`isFloat`
// records whether it carried a decimal point) so a suggestion can quote it; `calc` carries the
// inferred numeric type of a computed value (null when it can't be inferred) so it can be checked
// against the next operand in a chain — `shc * 0.25` is a decimal, so `+ 5` after it is a mix.
type Operand =
  | { kind: "path"; path: PathSegment[] }
  | { kind: "str"; value: string; pos: number }
  | { kind: "num"; raw: string; pos: number; isFloat: boolean }
  | { kind: "calc"; numeric: ScalarType | null }
  | { kind: "strList"; items: { value: string; pos: number }[] }
  | { kind: "none" };

const NONE: Operand = { kind: "none" };

// A short human label for an operand, for use inside a warning message.
function describeOperand(op: Operand): string {
  if (op.kind === "path") return `'${op.path.map((s) => s.value).join(".")}'`;
  if (op.kind === "num") return `'${op.raw}'`;
  return "the result";
}

// Strip a string token's surrounding quotes and unescape backslash escapes.
function unquoteString(raw: string): string {
  return raw.slice(1, -1).replace(/\\(.)/g, "$1");
}

class Parser {
  private i = 0;
  // Member-access paths used as values (e.g. `task.type` → [task, type]) and comprehension-bound
  // names. Paths are built in postfix(); a method call or dynamic index terminates a path, so
  // method names are never recorded (methods are ignored by variable/property checks).
  private readonly usedPaths: PathSegment[][] = [];
  private readonly boundVars = new Set<string>();
  // Non-fatal notes collected during checking (e.g. unknown properties of a known variable).
  // `options` is set only for enum mismatches — the accepted value set for that variable.
  readonly warnings: { message: string; kind: WarningKind; position: number; options?: readonly string[] }[] = [];

  constructor(
    private readonly tokens: Token[],
    private readonly end: number,
    private readonly cfg: DialectConfig,
    private readonly variables?: ReadonlySet<string>,
    private readonly schema?: VariableSchema,
  ) {}

  parse(): void {
    this.expression();
    const extra = this.tokens[this.i];
    if (extra) throw new ExprError(`Unexpected token '${extra.value}'`, extra.pos);
    // With a schema, validate full member-access paths (unknown roots → error, unknown
    // properties → warning). With a plain allow-list, only flag unknown root identifiers.
    if (this.schema) {
      for (const path of this.usedPaths) this.checkPath(path);
    } else if (this.variables) {
      for (const path of this.usedPaths) {
        const root = path[0];
        if (!root) continue;
        if (!this.variables.has(root.value) && !this.boundVars.has(root.value)) {
          throw new ExprError(`Unknown variable '${root.value}'`, root.pos);
        }
      }
    }
  }

  // Validate a member-access path against the schema. An unknown root is a hard error; an
  // unrecognised property of a known variable is recorded as a (non-fatal) warning.
  private checkPath(path: PathSegment[]): void {
    const root = path[0];
    if (!root) return;
    if (this.boundVars.has(root.value)) return;
    let node: SchemaNode | undefined = this.schema?.[root.value];
    if (node === undefined) throw new ExprError(`Unknown variable '${root.value}'`, root.pos);
    for (let k = 1; k < path.length; k++) {
      const seg = path[k];
      if (!seg) continue;
      if (isLeaf(node)) {
        const parent = path
          .slice(0, k)
          .map((s) => s.value)
          .join(".");
        this.warnings.push({
          message: `'${parent}' has no property '${seg.value}'`,
          kind: "property",
          position: seg.pos,
        });
        return;
      }
      const next: SchemaNode | undefined = node[seg.value];
      if (next === undefined) {
        const full = path
          .slice(0, k + 1)
          .map((s) => s.value)
          .join(".");
        this.warnings.push({ message: `Unknown property '${full}'`, kind: "property", position: seg.pos });
        return;
      }
      node = next;
    }
  }

  // Resolve a member-access operand to its schema node, or null when there is nothing to resolve
  // against: no schema, not a plain path, or an unknown / locally bound name (those are reported by
  // checkPath instead).
  private resolveNode(op: Operand): SchemaNode | null {
    if (!this.schema || op.kind !== "path") return null;
    const root = op.path[0];
    if (!root || this.boundVars.has(root.value)) return null;
    let node: SchemaNode | undefined = this.schema[root.value];
    for (let k = 1; k < op.path.length; k++) {
      const seg = op.path[k];
      if (!seg || node === undefined || isLeaf(node)) return null;
      node = node[seg.value];
    }
    return node ?? null;
  }

  // The enum value-set of a member-access operand, or null when its leaf isn't an enum.
  private enumValues(op: Operand): readonly string[] | null {
    const node = this.resolveNode(op);
    return isEnumLeaf(node) ? node : null;
  }

  // `enumPath == 'lit'` / `'lit' != enumPath`: the literal must be a member of the enum.
  private checkEnumComparison(a: Operand, b: Operand): void {
    const aVals = this.enumValues(a);
    if (aVals && b.kind === "str") {
      this.checkEnumMember(aVals, b, a);
      return;
    }
    const bVals = this.enumValues(b);
    if (bVals && a.kind === "str") this.checkEnumMember(bVals, a, b);
  }

  // `enumPath in ['lit', …]`: every string literal in the list must be a member of the enum.
  private checkEnumMembership(value: Operand, collection: Operand): void {
    const vals = this.enumValues(value);
    if (!vals || collection.kind !== "strList") return;
    for (const item of collection.items) this.checkEnumMember(vals, item, value);
  }

  // `floatPath > 5` / `5 == floatPath`: a decimal-valued variable compared against a whole-number
  // literal. CEL compares int and double operands fine, so this is not an error — but a bare `5`
  // against a fractional value is nearly always an authoring slip (and exact `==` on a float is
  // fragile), so the `.0` form is suggested. Only *literals* are flagged: a comparison against
  // another variable carries no such intent, and its type is the backend's business.
  private checkFloatComparison(a: Operand, b: Operand): void {
    const num = a.kind === "num" ? a : b.kind === "num" ? b : null;
    if (!num || num.isFloat) return;
    const path = a.kind === "path" ? a : b.kind === "path" ? b : null;
    if (!path || this.resolveNode(path) !== "float") return;
    // The label supplies the "decimal expected" framing, so the message is just the fix.
    this.warnings.push({
      message: `compare ${describeOperand(path)} against '${num.raw}.0', not '${num.raw}'`,
      kind: "decimal",
      position: num.pos,
    });
  }

  // Does this operand resolve to an object (a node with properties) rather than a value?
  private isObject(op: Operand): boolean {
    const node = this.resolveNode(op);
    return node !== null && !isLeaf(node);
  }

  // Report an object used where a value belongs, offering its field names as the accepted set.
  private reportObjectValue(op: Operand): void {
    const node = this.resolveNode(op);
    if (node === null || isLeaf(node)) return;
    // Point at the last segment — it names the object; any earlier segment is fine.
    const target = op.kind === "path" ? op.path[op.path.length - 1] : undefined;
    this.warnings.push({
      message: `compare a property of ${describeOperand(op)}, not the object itself`,
      kind: "object",
      position: target?.pos ?? 0,
      options: Object.keys(node).sort(),
    });
  }

  /**
   * `inventory > 1` and friends — an object where a value belongs. Ordering operators raise at
   * evaluation time; `==` / `!=` / `in` are worse, silently returning a constant, so the rule never
   * matches (or, for `!=`, always does). Comparing two objects for *equality* is left alone: CEL
   * compares maps structurally, so `store.open_time == store.close_time` genuinely works.
   */
  private checkObjectComparison(a: Operand, b: Operand, operator: Token): void {
    const equality = operator.value === "==" || operator.value === "!=";
    if (equality && this.isObject(a) && this.isObject(b)) return;
    for (const op of [a, b]) {
      if (this.isObject(op)) this.reportObjectValue(op);
    }
  }

  // The numeric type of an operand: a literal's own shape, a computed value's inferred type, or a
  // schema leaf typed "int"/"float". null means unknown (an untyped leaf, a method return, a string,
  // an unknown path) — nothing is inferred from it, so nothing is flagged against it.
  private numericType(op: Operand): ScalarType | null {
    if (op.kind === "num") return op.isFloat ? "float" : "int";
    if (op.kind === "calc") return op.numeric;
    const node = this.resolveNode(op);
    return node === "float" || node === "int" ? node : null;
  }

  /**
   * Check one arithmetic operation and return its result as a typed operand. Unlike a comparison,
   * CEL applies no numeric promotion here: `double + int` compiles fine and then raises a TypeError
   * mid-evaluation, so a rule that mixes the two is a live crash rather than a style slip. `%` is
   * additionally whole-number-only — a decimal operand fails even when *both* sides are decimals.
   */
  private checkArithmetic(left: Operand, right: Operand, operator: Token): Operand {
    const unchecked: Operand = { kind: "calc", numeric: null };
    // An object operand raises at evaluation time whatever the other side is, so report it and stop
    // typing this branch rather than also complaining about the numbers around it.
    if (this.isObject(left) || this.isObject(right)) {
      for (const op of [left, right]) {
        if (this.isObject(op)) this.reportObjectValue(op);
      }
      return unchecked;
    }
    const a = this.numericType(left);
    const b = this.numericType(right);
    if (operator.value === "%" && (a === "float" || b === "float")) {
      const decimal = a === "float" ? left : right;
      this.warnings.push({
        message: `'%' needs whole numbers, but ${describeOperand(decimal)} is a decimal`,
        kind: "arithmetic",
        position: operator.pos,
      });
      return unchecked;
    }
    if (a && b && a !== b) {
      const decimal = a === "float" ? left : right;
      const whole = a === "int" ? left : right;
      // A whole-number *literal* has a direct fix; a whole-number variable needs a conversion.
      const fix = whole.kind === "num" ? `use '${whole.raw}.0', not '${whole.raw}'` : "wrap one side in double()";
      this.warnings.push({
        message: `'${operator.value}' cannot mix ${describeOperand(decimal)} (a decimal) with ${describeOperand(whole)} (a whole number) — ${fix}`,
        kind: "arithmetic",
        position: operator.pos,
      });
      return unchecked;
    }
    // Both sides agree (or one is unknown, in which case the result is too).
    return { kind: "calc", numeric: a && b ? a : null };
  }

  private checkEnumMember(values: readonly string[], literal: { value: string; pos: number }, pathOp: Operand): void {
    if (values.includes(literal.value)) return;
    const name = pathOp.kind === "path" ? pathOp.path.map((s) => s.value).join(".") : "value";
    this.warnings.push({
      message: `'${literal.value}' is not a valid '${name}'`,
      kind: "value",
      position: literal.pos,
      options: values,
    });
  }

  // ── helpers ──
  private isOp(v: string): boolean {
    const t = this.tokens[this.i];
    return !!t && t.kind === "op" && t.value === v;
  }
  private isVal(v: string): boolean {
    return this.tokens[this.i]?.value === v;
  }
  private advance(): Token {
    const t = this.tokens[this.i];
    if (!t) throw new ExprError("Unexpected end of expression", this.end);
    this.i++;
    return t;
  }
  private expectOp(v: string): void {
    if (!this.isOp(v)) throw new ExprError(`Expected '${v}'`, this.tokens[this.i]?.pos ?? this.end);
    this.i++;
  }
  // A comprehension target (`for X in ...`) — a locally bound name, not a scope variable.
  private bindTarget(): void {
    const t = this.advance();
    if (t.kind !== "name" || this.cfg.keywords.has(t.value)) throw new ExprError("Expected a name", t.pos);
    this.boundVars.add(t.value);
  }

  // ── grammar (low → high precedence) ──
  // Each rule returns the Operand it parsed when it is a single atom (a variable path or a
  // string literal) with no operator applied at that level; once an operator combines terms the
  // result is a computed value (`none`). comparison() uses these operands to validate
  // enum-typed variables against the literals they're compared with.
  private expression(): Operand {
    const op = this.or();
    if (this.cfg.ternary && this.isOp("?")) {
      this.advance();
      this.expression();
      this.expectOp(":");
      this.expression();
      return NONE;
    }
    return op;
  }

  private or(): Operand {
    let op = this.and();
    while (this.isVal(this.cfg.or)) {
      this.advance();
      this.and();
      op = NONE;
    }
    return op;
  }

  private and(): Operand {
    let op = this.notExpr();
    while (this.isVal(this.cfg.and)) {
      this.advance();
      this.notExpr();
      op = NONE;
    }
    return op;
  }

  private notExpr(): Operand {
    if (this.cfg.notLowPrec && this.isVal(this.cfg.not)) {
      this.advance();
      this.notExpr();
      return NONE;
    }
    return this.comparison();
  }

  private comparison(): Operand {
    let left = this.additive();
    for (;;) {
      const t = this.tokens[this.i];
      if (t && t.kind === "op" && COMPARE_OPS.has(t.value)) {
        this.advance();
        const right = this.additive();
        if (t.value === "==" || t.value === "!=") this.checkEnumComparison(left, right);
        // Ordering comparisons matter here too: `inventory.soh > 0` is the common slip.
        this.checkFloatComparison(left, right);
        this.checkObjectComparison(left, right, t);
        left = NONE;
      } else if (t && t.kind === "name" && t.value === "in") {
        this.advance();
        const right = this.additive();
        this.checkEnumMembership(left, right);
        this.checkObjectComparison(left, right, t);
        left = NONE;
      } else if (this.cfg.keywordComparisons && t?.value === "not" && this.tokens[this.i + 1]?.value === "in") {
        this.advance(); // not
        this.advance(); // in
        const right = this.additive();
        this.checkEnumMembership(left, right);
        left = NONE;
      } else if (this.cfg.keywordComparisons && t?.value === "is") {
        // No object check on python's `is` / `is not`: `x is None` on an object is idiomatic there.
        this.advance(); // is
        if (this.isVal("not")) this.advance(); // is not
        this.additive();
        left = NONE;
      } else {
        break;
      }
    }
    return left;
  }

  private additive(): Operand {
    let op = this.multiplicative();
    while (this.isOp("+") || this.isOp("-")) {
      const operator = this.advance();
      op = this.checkArithmetic(op, this.multiplicative(), operator);
    }
    return op;
  }

  private multiplicative(): Operand {
    let op = this.unary();
    while (this.isOp("*") || this.isOp("/") || this.isOp("%")) {
      const operator = this.advance();
      op = this.checkArithmetic(op, this.unary(), operator);
    }
    return op;
  }

  private unary(): Operand {
    if (this.isOp("-")) {
      const minus = this.advance();
      const operand = this.unary();
      // A negated numeric literal is still a literal (`task.discrepancy_score > -1`), so carry it
      // through with the sign folded in. Anything else becomes a computed value that keeps its
      // numeric type, so `-inventory.soh + 1` is still caught as a mix.
      if (operand.kind === "num") return { ...operand, raw: `-${operand.raw}`, pos: minus.pos };
      return { kind: "calc", numeric: this.numericType(operand) };
    }
    if (!this.cfg.notLowPrec && this.isVal(this.cfg.not)) {
      this.advance();
      this.unary();
      return NONE;
    }
    return this.postfix();
  }

  private postfix(): Operand {
    const root = this.primary();
    // Build the member-access path from the primary's root path. Method calls and dynamic
    // indexing terminate the path (set it to null) so methods/computed members are not checked.
    let path: PathSegment[] | null = root.kind === "path" ? [...root.path] : null;
    let consumed = false;
    for (;;) {
      if (this.isOp(".")) {
        consumed = true;
        this.advance();
        const name = this.advance();
        if (name.kind !== "name" || this.cfg.keywords.has(name.value)) throw new ExprError("Expected a name", name.pos);
        // A `(` immediately after means this is a method call: the method name itself is ignored,
        // but the property path accumulated up to here is still validated. Stop tracking after it
        // (the method's return value is unchecked); the call is consumed on the next iteration.
        if (this.isOp("(")) {
          if (path) this.usedPaths.push(path);
          path = null;
        } else if (path) {
          path.push({ value: name.value, pos: name.pos });
        }
      } else if (this.isOp("(")) {
        consumed = true;
        this.advance();
        this.args(")");
        path = null; // the value itself is being called (a function) — call target, exempt
      } else if (this.isOp("[")) {
        // Validate the path up to here, then stop (the indexed element's type is unchecked).
        consumed = true;
        if (path) this.usedPaths.push(path);
        path = null;
        this.advance();
        this.expression();
        this.expectOp("]");
      } else {
        break;
      }
    }
    if (path) {
      this.usedPaths.push(path);
      return { kind: "path", path };
    }
    // A postfix chain that terminated the path yields a computed value; an untouched atom
    // (string literal / list / number) passes through so comparison() can inspect it.
    return consumed ? NONE : root;
  }

  private args(close: string): void {
    if (this.isOp(close)) {
      this.advance();
      return;
    }
    this.expression();
    while (this.isOp(",")) {
      this.advance();
      if (this.isOp(close)) break;
      this.expression();
    }
    this.expectOp(close);
  }

  // Parses a primary and returns it as an Operand. A bare value-name becomes a single-segment
  // path (postfix extends it); string literals, numeric literals and pure string-literal lists are
  // returned so comparison() can check them against the schema; everything else is `none`.
  private primary(): Operand {
    const t = this.tokens[this.i];
    if (!t) throw new ExprError("Unexpected end of expression", this.end);
    if (t.kind === "num") {
      this.advance();
      return { kind: "num", raw: t.value, pos: t.pos, isFloat: t.value.includes(".") };
    }
    if (t.kind === "str") {
      this.advance();
      return { kind: "str", value: unquoteString(t.value), pos: t.pos };
    }
    if (t.kind === "name") {
      if (this.cfg.booleans.has(t.value)) {
        this.advance();
        return NONE;
      }
      if (this.cfg.keywords.has(t.value)) throw new ExprError(`Unexpected keyword '${t.value}'`, t.pos);
      this.advance();
      return { kind: "path", path: [{ value: t.value, pos: t.pos }] };
    }
    if (t.value === "(") {
      this.advance();
      const inner = this.expression(); // propagate so `(enum) == 'x'` is still checked
      this.expectOp(")");
      return inner;
    }
    if (t.value === "[") {
      return this.list();
    }
    if (t.value === "{") {
      this.braces();
      return NONE;
    }
    throw new ExprError(`Unexpected token '${t.value}'`, t.pos);
  }

  // Returns a `strList` operand when the list is a plain collection of string literals
  // (`['a', 'b']`) — the form used for `enum in [...]` checks; otherwise `none`.
  private list(): Operand {
    this.advance(); // [
    if (this.isOp("]")) {
      this.advance();
      return { kind: "strList", items: [] };
    }
    const first = this.expression();
    if (this.cfg.comprehension && this.isVal("for")) {
      while (this.isVal("for")) {
        this.advance();
        this.bindTarget();
        while (this.isOp(",")) {
          this.advance();
          this.bindTarget();
        }
        if (!this.isVal("in")) throw new ExprError("Expected 'in'", this.tokens[this.i]?.pos ?? this.end);
        this.advance();
        this.or();
        while (this.isVal("if")) {
          this.advance();
          this.or();
        }
      }
      this.expectOp("]");
      return NONE;
    }
    const items: { value: string; pos: number }[] = [];
    let pure = first.kind === "str";
    if (first.kind === "str") items.push({ value: first.value, pos: first.pos });
    while (this.isOp(",")) {
      this.advance();
      if (this.isOp("]")) break;
      const el = this.expression();
      if (el.kind === "str") items.push({ value: el.value, pos: el.pos });
      else pure = false;
    }
    this.expectOp("]");
    return pure ? { kind: "strList", items } : NONE;
  }

  private braces(): void {
    this.advance(); // {
    if (this.isOp("}")) {
      this.advance();
      return;
    }
    this.expression();
    if (this.isOp(":")) {
      // map / dict
      this.advance();
      this.expression();
      while (this.isOp(",")) {
        this.advance();
        if (this.isOp("}")) break;
        this.expression();
        this.expectOp(":");
        this.expression();
      }
      this.expectOp("}");
      return;
    }
    // set literal (python only; CEL has no set literal)
    if (!this.cfg.comprehension) throw new ExprError("Expected ':' in map", this.tokens[this.i]?.pos ?? this.end);
    while (this.isOp(",")) {
      this.advance();
      if (this.isOp("}")) break;
      this.expression();
    }
    this.expectOp("}");
  }
}

/**
 * Validate an expression's syntax for the given dialect (defaults to python). The optional
 * `scope` constrains which variables may be referenced:
 * - a `string[]` allow-list flags any unknown root identifier as an error;
 * - a {@link VariableSchema} additionally checks member-access paths — an unknown root is an
 *   error, while an unrecognised property of a known variable, a comparison of an enum-typed
 *   variable against a value outside its set (`day_of_week == 'Frobday'`, `x in ['Mon', …]`), a
 *   comparison of a `"float"` leaf against a whole-number literal (`inventory.soh > 0`), or
 *   arithmetic CEL will reject at evaluation time (`inventory.soh * 2`, `inventory.soh % 2.0`), is
 *   reported as a (non-fatal) `warning` (the expression stays `valid`; the accepted values, if any,
 *   come back in `warningOptions`). Grammar is the only hard failure — everything the schema
 *   contributes is advisory, because the schema is a hand-maintained mirror of the backend models
 *   and must never block a rule on its own drift. `warningKind` reports which check fired.
 * Call targets, method names and comprehension-bound names are always exempt.
 *
 * Planned (CORE-4072, deferred deliberately — don't "fix" it either way): once the checks have run
 * in the field long enough to be trusted, the crash-level kinds (`"arithmetic"` and `"object"`) are
 * meant to block saving a rule, since those are engine-verified failures rather than style hints.
 * Until then a wrong numeric type in the schema would stop the team saving a good rule with no
 * override, which is worse than letting a bad rule through. `"decimal"` stays advisory regardless.
 */
export function validateExpression(
  expression: string,
  dialect: ExpressionDialect = "python",
  scope?: readonly string[] | VariableSchema,
): ExpressionValidation {
  const text = expression.trim();
  if (!text) return { valid: false };
  const variables = Array.isArray(scope) ? new Set(scope) : undefined;
  const schema = scope && !Array.isArray(scope) ? (scope as VariableSchema) : undefined;
  try {
    const tokens = tokenize(text);
    if (tokens.length === 0) return { valid: false };
    const parser = new Parser(tokens, text.length, CONFIGS[dialect], variables, schema);
    parser.parse();
    // Only one warning is surfaced, so rank by severity before picking: arithmetic will crash the
    // evaluation, an unknown property or bad enum value means the rule silently won't match, and the
    // decimal hint is cosmetic. `sort` is stable, so equal ranks keep their left-to-right order.
    const warning = [...parser.warnings].sort((x, y) => WARNING_RANK[x.kind] - WARNING_RANK[y.kind])[0];
    if (warning) {
      return {
        valid: true,
        warning: warning.message,
        warningKind: warning.kind,
        position: warning.position,
        warningOptions: warning.options,
      };
    }
    return { valid: true };
  } catch (e) {
    if (e instanceof ExprError) return { valid: false, error: e.message, position: e.position };
    return { valid: false, error: (e as Error).message };
  }
}

// Count occurrences of a quote char not preceded by a backslash — used to tell whether the cursor
// sits inside an (unterminated) string literal.
function countUnescaped(text: string, quote: string): number {
  let count = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === quote && text[i - 1] !== "\\") count++;
  }
  return count;
}

/**
 * Editor helper: given the text up to the cursor, return the schema object whose fields are in scope
 * for the member access being typed to the left (the object before the current `a.b.c` path), plus
 * its field names — for a read-only "available fields" hint under the editor.
 *
 * Returns null when there's nothing useful to surface: a bare top-level identifier (already covered
 * by the root variable list), a cursor inside a string literal, or a path that resolves to a
 * scalar/enum/unknown node. Does NOT filter by a partially-typed segment — it always lists every
 * field of the object in context.
 */
export function fieldsAtCursor(
  schema: VariableSchema,
  textBeforeCursor: string,
): { path: string; fields: string[] } | null {
  // Don't suggest inside a string literal (odd number of unescaped quotes of either kind).
  if (countUnescaped(textBeforeCursor, '"') % 2 === 1 || countUnescaped(textBeforeCursor, "'") % 2 === 1) {
    return null;
  }
  // Trailing member-access path: dot-joined identifiers, optionally ending on a dot.
  const match = textBeforeCursor.match(/([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*\.?)$/);
  if (!match?.[1]) return null;
  const raw = match[1];
  const segments = raw.split(".").filter((s) => s.length > 0);
  // The object we list fields of: the whole path when it ends on a dot, otherwise everything but the
  // segment currently being typed. A bare root (nothing before the typed segment) adds nothing new.
  const base = raw.endsWith(".") ? segments : segments.slice(0, -1);
  if (base.length === 0) return null;
  const root = base[0];
  if (!root) return null;
  let node: SchemaNode | undefined = schema[root];
  for (let k = 1; k < base.length; k++) {
    const seg = base[k];
    if (!seg || node === undefined || isLeaf(node)) return null;
    node = node[seg];
  }
  // Only objects have fields to offer; scalar, typed-scalar, enum and unknown leaves don't.
  if (node === undefined || isLeaf(node)) return null;
  return { path: base.join("."), fields: Object.keys(node).sort() };
}
